import {
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  Select,
  SelectChangeEvent,
  SelectProps,
} from '@mui/material';
import { ReactNode } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { formFieldLabel } from '@componentsroot/Form/FormGenerator/utils';

type TValue = string | string[] | undefined;

type TReactHookFormSelectProps = {
  name: string;
  label: string;
  control: Control<FieldValues, TValue>;
  defaultValue?: TValue;
  required?: boolean;
  onOptionChange?: (event: SelectChangeEvent) => void;
  children: ReactNode;
  // see on why https://github.com/mui/material-ui/issues/18580
  // Select component does not support all margin property like other components,
  // it must be put into FormControl component to support margin property.
  margin?: FormControlProps['margin'];
  size?: SelectProps<TValue>['size'];
} & Omit<SelectProps<TValue>, 'margin'>;

export default function FormSelect({
  name,
  label,
  control,
  onOptionChange,
  required = false,
  defaultValue = '',
  margin = 'normal',
  size = 'medium',
  children,
  sx,
  ...selectProps
}: TReactHookFormSelectProps) {
  const requiredLabel = formFieldLabel(label, required);
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        return (
          <>
            <FormControl
              sx={{ flex: '1', ...sx }}
              fullWidth
              error={!!fieldState.error?.message}
              margin={margin}
              // The label positions its unshrunk state for the height of the input it sits in, and
              // took that height from its own default rather than from the Select. On a small form
              // it therefore sat below the middle. Not new - `defaultValue = ''` predates this
              // ticket and MedicareCardEditor's colour Select can already open empty - but VME-4061
              // is where it was noticed, because removing the State default made the driver licence
              // form open with an empty Select on the screen an operator uses every day. Giving the
              // size to the FormControl hands it to the label and the Select together.
              size={size}
            >
              <InputLabel>{requiredLabel}</InputLabel>
              <Select
                {...(selectProps as SelectProps<string>)}
                {...field}
                size={size}
                onChange={(event: SelectChangeEvent) => {
                  field.onChange(event);

                  // The custom onOptionChange must be called last to allow field level onChange event propagate first.
                  onOptionChange?.(event);
                }}
                label={requiredLabel}
              >
                {children}
              </Select>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          </>
        );
      }}
    />
  );
}
